
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderActiveMQIntegration () {
    const colorMap = {
  blue: "bg-blue-100 dark:bg-blue-900/30",
  green: "bg-green-100 dark:bg-green-900/30",
  purple: "bg-purple-100 dark:bg-purple-900/30",
  orange: "bg-orange-100 dark:bg-orange-900/30",
  indigo: "bg-indigo-100 dark:bg-indigo-900/30",
  pink: "bg-pink-100 dark:bg-pink-900/30",
  cyan: "bg-cyan-100 dark:bg-cyan-900/30",
  yellow: "bg-yellow-100 dark:bg-yellow-900/30",
};
    return (
      <div> 
        <main className="transition-opacity duration-300">
          <div className="py-4">
    
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
              <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
              <FiChevronRight />
              <span>Agent Setup Guide</span>
              <FiChevronRight />
              <span>Message Queue</span>
              <FiChevronRight />
              <span className="text-[#1b140d] dark:text-white font-medium">ActiveMQ</span>
            </nav>
            {/* Intro */}
            <div className="mb-10" id="Active MQ Integration">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  Active MQ Integration
                </span>
              </h1>
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This guide explains the steps of Active MQ integration .
              </p>
            </div>
            {[
              {
                step: "1",
                color: "blue",
                title: "Configure ActiveMQ",
                content: (
                  <>
                    <p>In the ActiveMQ folder inside the /conf directory in the activemq.xml file edit or add this configuration:</p>
                    <CodeBlock code={`<broker xmlns="http://activemq.apache.org/schema/core" brokerName="BROKER1" 	useJmx="true" dataDirectory="\${activemq.data}">`} />
                  </>
                )
              },
              {
                step: "2",
                color: "green",
                title: "Create Directory",
                content: (
                  <>
                    <p>Create a directory named JMX_Exporter</p>
                  </>
                )
              },
              {
                step: "3",
                color: "purple",
                title: "Download JMX Exporter",
                content: (
                  <>
                    <p>Inside this folder download JMX exporter</p>
                    <CodeBlock code={`wget https://github.com/prometheus/jmx_exporter/releases/download/0.20.0/jmx_prometheus_javaagent-0.20.0.jar`} />
                  </>
                )
              },
              {
                step: "4",
                color: "orange",
                title: "Create Config File",
                content: (
                  <>
                    <p>Inside the same folder create a file jmx_exporter_config.xml</p>
                    <CodeBlock code={`startDelaySeconds: 10
    lowercaseOutputName: false
    lowercaseOutputLabelNames: false
    ssl: false
    includeObjectNames:
      - "org.apache.activemq:*"
      - "java.lang:type=OperatingSystem"
      - "java.lang:type=GarbageCollector,*"
      - "java.lang:type=Threading"
      - "java.lang:type=Runtime"
      - "java.lang:type=Memory"
      - "java.lang:name=*"
    rules:
      - pattern: ".*"`} />
                  </>
                )
              },
              {
                step: "5",
                color: "indigo",
                title: "Attach Exporter as Java Agent",
                content: (
                  <>
                    <p>Attach Exporter as a Java Agent</p>
                    <p>In bin/env (ActiveMQ startup environment file), find the ACTIVEMQ_OPTS variable and append:</p>
                    <CodeBlock code={`ACTIVEMQ_OPTS="$ACTIVEMQ_OPTS -javaagent:/path/to/jmx_prometheus_javaagent-0.20.0.jar=9404:/path/to/jmx_exporter_config.yaml"`} />
    
                    <p>Example - ACTIVEMQ_OPTS="$ACTIVEMQ_OPTS $ACTIVEMQ_SUNJMX_START -javaagent:$HOME/JMX_Exporter/jmx_prometheus_javaagent-0.20.0.jar=9404:$HOME/JMX_Exporter/jmx_exporter_config.yaml"</p>
    
                    <CodeBlock code={`ACTIVEMQ_SUNJMX_START="-Dcom.sun.management.jmxremote \\
    -Dcom.sun.management.jmxremote.port=1616 \\
    -Dcom.sun.management.jmxremote.rmi.port=1616 \\
    -Djava.rmi.server.hostname=127.0.0.1 \\
    -Dcom.sun.management.jmxremote.authenticate=false \\
    -Dcom.sun.management.jmxremote.ssl=false"`} />
                  </>
                )
              },
              {
                step: "6",
                color: "pink",
                title: "Start ActiveMQ",
                content: (
                  <>
                    <p>Start ActiveMQ - bin/activemq start</p>
                  </>
                )
              },
              {
                step: "7",
                color: "cyan",
                title: "Trouble shooting steps",
                content: (
                  <>
                    <p>a. If you want to stop the ActiveMQ</p>
                    <CodeBlock code={`bin/activemq stop`} />
    
                    <p>b. Check –</p>
                    <CodeBlock code={`ps -ef | grep activemq`} />
    
                    <p>c. Check the log file for ActiveMQ</p>
                    <CodeBlock code={`cat /home/hitesh.mishra@apmosys.mahape/apache-activemq-5.18.3/data/activemq.log | tail -n 30`} />
    
                    <p>d.   If the configs are not working then start ActiveMQ by running</p>
                    <CodeBlock code={`/usr/bin/java   -javaagent:$HOME/JMX_Exporter/jmx_prometheus_javaagent-0.20.0.jar=9404:$HOME/JMX_Exporter/jmx_exporter_config.yaml   -Dcom.sun.management.jmxremote   -Dcom.sun.management.jmxremote.port=1616   -Dcom.sun.management.jmxremote.rmi.port=1616   -Djava.rmi.server.hostname=127.0.0.1 -Dcom.sun.management.jmxremote.authenticate=false   -Dcom.sun.management.jmxremote.ssl=false   -jar bin/activemq.jar start`} />
                  </>
                )
              },
              {
                step: "8",
                color: "yellow",
                title: "Configure OTEL Collector",
                content: (
                  <>
                    <p>Download the otelcol config file from the provided apmosys share point and edit the below line</p>
    
                    <CodeBlock code={`receivers:
      prometheus:
        config:
          scrape_configs:
            - job_name: "activemq-monitoring"
              scrape_interval: 10s
              static_configs:
                - targets: ['<system IP>:9404']
                  labels:
                    cluster: activemq-cluster
    
    exporters:
      otlp:
        endpoint: <AOP IP>:11800
        tls:
          insecure: true
    
    processors:
      batch:
    service:
      pipelines:
        metrics:
          receivers:
            - prometheus
          processors:
            - batch
          exporters:
            - otlp`} />
    
                    <p>If you have already installed infra agent for host monitoring in the server where the ActiveMQ is running, then you can use the existing config.yaml file in the agent folder. Add a block of scrape config command in config block as shown below:</p>
    
                    <CodeBlock code={`      scrape_configs:
            - job_name: "activemq-monitoring"
              scrape_interval: 10s
              static_configs:
                - targets: ['<system IP>:9404']`} />
                  </>
                )
              },
              {
                step: "9",
                color: "red",
                title: "Start OTEL & Verify Metrics",
                content: (
                  <>
                    <p>Start otelcol</p>
                    <CodeBlock code={`./otelcol –config=config.yaml`} />
    
                    <p>To check whether the metrics are being exposed to the specified port or not hit the below curl command in the terminal:</p>
                    <CodeBlock code={`curl http://<system IP>:9404/metrics | head`} />
    
                    <p>To check over a browser hit the below url:</p>
                    <CodeBlock code={`http://<system IP>:9404/metrics`} />
                  </>
                )
              }
            ].map((item, idx) => (
              <section key={idx} className="mb-12">
                <h2 className="flex items-center gap-3 text-2xl font-bold mb-6">
                  <span className={`w-10 h-10 flex items-center justify-center rounded-lg bg-${item.color}-100 dark:bg-${item.color}-900/30`}>
                    {item.step}
                  </span>
                  {item.title}
                </h2>
    
                <div className="text-sm text-text-muted space-y-2 whitespace-pre-line">
                  {item.content}
                </div>
              </section>
            ))}
    
          </div>
        </main>
      </div>
    );
    }